import React, { useEffect, useState } from 'react';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import { Sprite } from '../Sprites/Sprite';
import './PremiumFinancing.scss';

/**
 * 「什麼是保費融資」, an in-house replacement for the Piktochart (PF01) of the same name, in the look of
 * ai.portfoplus.com. It keeps the Piktochart's two diagrams made of blocks (saving up a premium over
 * 25 years, against paying a down payment and borrowing the rest for 10 years, then taking the interest
 * off to get the net return) and its six advantages. The diagrams are about the idea, not about figures.
 * Pointing at a step of the financing diagram says what each block is; a button walks through them.
 */

/** The financing diagram, in the order the steps build it up: what each part is. */
const STEPS = ['down', 'loan', 'value', 'interest', 'net'] as const;
type Step = (typeof STEPS)[number];

const ADVANTAGES = [
  { n: 1, Icon: TrendingUpRoundedIcon },
  { n: 2, Icon: PercentRoundedIcon },
  { n: 3, Icon: WaterDropRoundedIcon },
  { n: 4, Icon: VerifiedRoundedIcon },
  { n: 5, Icon: TaskAltRoundedIcon },
  { n: 6, Icon: TuneRoundedIcon }
];

/** What the two ways can be compared on. */
const METRICS = ['own', 'time', 'value', 'cost'] as const;
type Metric = (typeof METRICS)[number];
type Side = 'saving' | 'financing';
const SIDES: Side[] = ['saving', 'financing'];

/** The years each way takes in the Piktochart's diagrams. */
const YEARS: Record<Side, number> = { saving: 25, financing: 10 };

/** How long the walk-through rests on each step. */
const STEP_MS = 1800;

/**
 * The blocks of a diagram; `tones` is one letter a block: m (own money), l (the loan), s (the saved premium).
 * `faint` says which blocks the walk-through has not reached yet; they are drawn pale, so the diagram keeps its shape.
 */
const Blocks = ({
  tones,
  columns,
  faint = () => false
}: {
  tones: string;
  columns: number;
  faint?: (tone: string) => boolean;
}) => (
  <div className="pf-blocks" style={{ '--cols': columns } as React.CSSProperties}>
    {tones.split('').map((tone, i) => (
      <span key={i} className={`pf-block is-${tone}${faint(tone) ? ' is-faint' : ''}`} />
    ))}
  </div>
);

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const PremiumFinancing = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  // How many steps of the financing diagram are drawn; all five to begin with, as the Piktochart was.
  const [step, setStep] = useState<number>(STEPS.length);
  const [playing, setPlaying] = useState(false);
  // Whether a walk-through has run to its end, so the button can say Replay.
  const [played, setPlayed] = useState(false);
  const [metric, setMetric] = useState<Metric>('own');

  useEffect(() => {
    if (!playing) return;
    if (step >= STEPS.length) {
      setPlaying(false);
      setPlayed(true);
      return;
    }
    const timer = window.setTimeout(() => setStep(step + 1), step === 0 ? 400 : STEP_MS);
    return () => window.clearTimeout(timer);
  }, [playing, step]);

  const shown = (name: Step) => step >= STEPS.indexOf(name) + 1;
  const pick = (n: number) => {
    setPlaying(false);
    setStep(n);
  };
  const togglePlay = () => {
    if (playing) return setPlaying(false);
    if (step >= STEPS.length) setStep(0);
    setPlaying(true);
  };
  const playLabel = playing ? 'pf-pause' : played && step >= STEPS.length ? 'pf-replay' : 'pf-play';
  const current: Step = STEPS[Math.max(step, 1) - 1];

  return (
    <div className="premium-financing" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="pf-head">
        <h2 className="pf-title">
          <span>{t('pf-title')}</span>
        </h2>
      </header>

      <section className="pf-block-card is-saving">
        <h3>{t('pf-savings')}</h3>
        <div className="pf-flow">
          <figure>
            <Blocks tones="s" columns={1} />
            <figcaption>{t('pf-premium')}</figcaption>
          </figure>
          <div className="pf-arrow">
            <span>{t('pf-years', { n: 25 })}</span>
          </div>
          <figure>
            <Blocks tones="ssss" columns={2} />
            <figcaption>{t('pf-value')}</figcaption>
          </figure>
        </div>
      </section>

      <section className="pf-block-card is-financing" aria-live="polite">
        {/* The play button sits beside the title, so it never wraps onto a row of its own under the steps. */}
        <div className="pf-card-head">
          <h3>{t('pf-financing')}</h3>
          <button type="button" className="pf-play" onClick={togglePlay}>
            {playing ? <PauseRoundedIcon aria-hidden="true" /> : <PlayArrowRoundedIcon aria-hidden="true" />}
            {t(playLabel)}
          </button>
        </div>

        <div className="pf-steps" role="group" aria-label={t('pf-financing')}>
          {STEPS.map((name, i) => (
            <button
              key={name}
              type="button"
              className={`${step >= i + 1 ? 'is-on' : ''}${current === name && step > 0 ? ' is-current' : ''}`}
              aria-pressed={step === i + 1}
              onClick={() => pick(i + 1)}
            >
              <span>{i + 1}</span>
              {t(`pf-${name}`)}
            </button>
          ))}
        </div>

        <div className="pf-flow is-financing">
          <figure className="pf-start">
            <figcaption className="is-top">{t('pf-down')}</figcaption>
            {/* One block of your own money and three of the loan, as in the Piktochart. */}
            <Blocks tones="mlll" columns={2} faint={tone => !shown(tone === 'm' ? 'down' : 'loan')} />
            <figcaption>{t('pf-loan')}</figcaption>
          </figure>
          <div className={`pf-arrow is-up${shown('value') ? ' is-shown' : ''}`}>
            <span>{t('pf-years', { n: 10 })}</span>
          </div>
          <figure className={`pf-end${shown('value') ? ' is-shown' : ''}`}>
            <Blocks tones="mmmlll" columns={2} />
            <figcaption>{t('pf-value')}</figcaption>
          </figure>
          <div className="pf-net">
            <Sprite name="piggy-coin" size={60} className={`pf-money${shown('net') ? ' is-shown' : ''}`} />
            <span className="pf-formula">
              <span className={`pf-minus${shown('interest') ? ' is-shown' : ''}`}>− {t('pf-interest')}</span>
              <span className={`pf-equals${shown('net') ? ' is-shown' : ''}`}>
                = <strong>{t('pf-net')}</strong>
              </span>
            </span>
          </div>
        </div>

        <p className="pf-step-note">{step > 0 ? t(`pf-step-${current}`) : t('pf-step-start')}</p>
        <div className="pf-legend">
          <span className="is-m">{t('pf-down')}</span>
          <span className="is-l">{t('pf-loan')}</span>
        </div>
      </section>

      <section className="pf-block-card is-versus" aria-live="polite">
        <h3>{t('pf-cmp-title')}</h3>
        <p className="pf-sub">{t('pf-cmp-sub')}</p>
        <div className="pf-chips" role="group" aria-label={t('pf-cmp-title')}>
          {METRICS.map(m => (
            <button
              key={m}
              type="button"
              className={m === metric ? 'is-on' : ''}
              aria-pressed={m === metric}
              onClick={() => setMetric(m)}
            >
              {t(`pf-cmp-${m}`)}
            </button>
          ))}
        </div>
        <div className="pf-versus">
          {SIDES.map(side => (
            <div key={side} className={`pf-side is-${side}`}>
              <div className="pf-side-head">
                <Sprite name={side === 'saving' ? 'piggy-coin' : 'lever-coin'} size={52} bob={side === 'saving'} />
                <strong>{t(side === 'saving' ? 'pf-savings' : 'pf-financing')}</strong>
              </div>
              <div className={`pf-side-visual is-${metric}`}>
                {metric === 'own' && (
                  <Blocks tones={side === 'saving' ? 's' : 'mlll'} columns={side === 'saving' ? 1 : 2} />
                )}
                {metric === 'value' && <Blocks tones={side === 'saving' ? 'ssss' : 'mmmlll'} columns={2} />}
                {metric === 'time' && (
                  <div className="pf-timebar">
                    <span style={{ width: `${(YEARS[side] / YEARS.saving) * 100}%` }}>
                      {t('pf-years', { n: YEARS[side] })}
                    </span>
                  </div>
                )}
                {metric === 'cost' &&
                  (side === 'saving' ? (
                    <CheckCircleRoundedIcon className="pf-cost is-none" aria-hidden="true" />
                  ) : (
                    <PaidRoundedIcon className="pf-cost is-interest" aria-hidden="true" />
                  ))}
              </div>
              <p>{t(`pf-cmp-${metric}-${side}`)}</p>
            </div>
          ))}
        </div>
        <p className="pf-cmp-summary">{t(`pf-cmp-sum-${metric}`)}</p>
      </section>

      <h2 className="pf-title is-sub">
        <span>{t('pf-adv-title')}</span>
      </h2>
      <ul className="pf-advantages">
        {ADVANTAGES.map(({ n, Icon }) => (
          <li key={n}>
            <span className="pf-adv-icon" aria-hidden="true">
              <Icon />
            </span>
            <h3>{t(`pf-b${n}-t`)}</h3>
            <p>{t(`pf-b${n}-d`)}</p>
          </li>
        ))}
      </ul>

      <p className="pf-risk">{t('pf-risk')}</p>
      <p className="pf-disclaimer">
        {t('pf-disclaimer-1')}
        <br />
        {t('pf-disclaimer-2')}
      </p>
    </div>
  );
};

export default PremiumFinancing;
